import { FontWeight } from '@pasteltech/theme-provider'
import React, { memo } from 'react'
import styled from 'styled-components'
import { PagePaths } from '../../constants'
import { ChevronBackwardIcon } from '../../svg-components'
import { DeepNullable, ScreenQuery } from '../../utilities'
import { CheckMoreButton } from '../button'
import { StrapiSvg, StrapiSvgData } from '../strapi-svg'

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.container.high};
  background-image: url('/images/home-page-service-process-background.png');
  background-position: left top;
  background-repeat: no-repeat;

  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  padding: ${({ theme }) => theme.sizes.spacings.xxxxxl}px 0;
  margin: 0 auto;
`
const Heading = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.heading1}px;
  font-weight: ${FontWeight.bold};
`
const SubHeading = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.heading3}px;
  font-weight: ${FontWeight.bold};
`
const Title = styled.div`
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.subtitle}px;
  font-weight: ${FontWeight.medium};
`

const StepsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
  align-self: stretch;
  ${ScreenQuery.mobile} {
    flex-direction: column;
  }
  ${ScreenQuery.desktop} {
    margin: 0 auto;
    width: 70%;
  }
`
const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  padding: ${({ theme }) => theme.sizes.spacings.lg}px;
  background: ${({ theme }) => theme.colors.container.lowest};
`
const Step = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) =>
    `${theme.sizes.spacings.xl}px ${theme.sizes.spacings.lg}px ${theme.sizes.spacings.xxxxl}px`};
  gap: ${({ theme }) => theme.sizes.spacings.xxl}px;
`
const StepTitleAndDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.spacings.sm}px;
  white-space: nowrap;
`
const StepDescription = styled.div`
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  font-size: ${({ theme }) => theme.sizes.fontSize.subtitle}px;
  font-weight: ${FontWeight.medium};
`
const StepTitle = styled.div`
  color: ${({ theme }) => theme.colors.primary.containerContrast};
  text-align: center;
  font-size: ${({ theme }) => theme.sizes.fontSize.title}px;
  font-weight: ${FontWeight.bold};
`

const StyledChevronBackwardIcon = styled(ChevronBackwardIcon)`
  ${ScreenQuery.mobile} {
    transform: rotate(90deg);
  }
`
type ServiceStep = null | DeepNullable<{
  id: string
  title: string
  description: string
  icon?: StrapiSvgData
}>
type ServiceProcess = null | DeepNullable<{
  title: string
  heading: string
  subHeading: string
  steps: ServiceStep[]
}>
type Props = {
  data: ServiceProcess
}

export const ServiceProcessSection = memo(({ data }: Props) => {
  const steps = data?.steps ?? []
  return (
    <Container>
      <Title>{data?.title}</Title>
      <Heading>{data?.heading}</Heading>
      <SubHeading>{data?.subHeading}</SubHeading>

      <StepsContainer>
        {steps.map((step, index) => {
          return (
            <React.Fragment key={step?.id}>
              <Step>
                <IconContainer>
                  <StrapiSvg strapiSvg={step?.icon} isRequired />
                </IconContainer>
                <StepTitleAndDescriptionContainer>
                  <StepDescription>{step?.description}</StepDescription>
                  <StepTitle>{step?.title}</StepTitle>
                </StepTitleAndDescriptionContainer>
              </Step>
              {index < steps.length - 1 && <StyledChevronBackwardIcon />}
            </React.Fragment>
          )
        })}
      </StepsContainer>
      <CheckMoreButton to={PagePaths.services} />
    </Container>
  )
})

ServiceProcessSection.displayName = 'ServiceProcessSection'
